import { useEffect, useMemo, useState } from "react";
import { Car, Clock, FileDown, MapPin, Plus, Save, TicketCheck } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader } from "../../components/shared";

const emptyArea = { name: "", location: "", level: "" };
const emptySlot = {
  slot_number: "",
  area_id: "",
  location: "",
  level: "",
  slot_type: "CAR",
  allowed_vehicle_types: ["CAR"],
  active: true,
};
const emptyAllocation = {
  slot_id: "",
  unit_id: "",
  vehicle_id: "",
  person_id: "",
  allocation_type: "PERMANENT",
  start_date: "",
  end_date: "",
  reason: "",
};
const emptyTemporary = {
  unit_id: "",
  person_id: "",
  vehicle_id: "",
  registration_number: "",
  slot_id: "",
  valid_from: "",
  valid_until: "",
  reason: "",
};
const emptyPass = {
  pass_number: "",
  vehicle_id: "",
  slot_id: "",
  expiry_date: "",
};

const RELATIONSHIPS = [
  "OWNER",
  "JOINT_OWNER",
  "TENANT",
  "FAMILY_MEMBER",
  "AUTHORIZED_OCCUPANT",
];
const SLOT_STATUS_OPTIONS = [
  "AVAILABLE",
  "RESERVED",
  "BLOCKED",
  "MAINTENANCE",
  "INACTIVE",
];
const WAITING_STATUS_OPTIONS = [
  "WAITING",
  "OFFERED",
  "ALLOCATED",
  "WITHDRAWN",
  "CANCELLED",
  "INELIGIBLE",
];

function Status({ value }) {
  const color =
    {
      AVAILABLE: "bg-emerald-100 text-emerald-800",
      ALLOCATED: "bg-blue-100 text-blue-800",
      RESERVED: "bg-indigo-100 text-indigo-800",
      BLOCKED: "bg-rose-100 text-rose-800",
      MAINTENANCE: "bg-amber-100 text-amber-800",
      INACTIVE: "bg-slate-200 text-slate-700",
      PENDING_VERIFICATION: "bg-amber-100 text-amber-800",
      APPROVED: "bg-emerald-100 text-emerald-800",
      REJECTED: "bg-rose-100 text-rose-800",
      ACTIVE: "bg-emerald-100 text-emerald-800",
      RELEASED: "bg-slate-100 text-slate-700",
      REVOKED: "bg-rose-100 text-rose-800",
      WAITING: "bg-amber-100 text-amber-800",
      OFFERED: "bg-blue-100 text-blue-800",
    }[value] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`rounded-full px-2 py-1 text-xs font-semibold ${color}`}
      data-testid={`parking-status-${String(value).toLowerCase()}`}
    >
      {String(value || "UNKNOWN").replaceAll("_", " ")}
    </span>
  );
}

export default function SocietyParking() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [area, setArea] = useState(emptyArea);
  const [slot, setSlot] = useState(emptySlot);
  const [allocation, setAllocation] = useState(emptyAllocation);
  const [settings, setSettings] = useState(null);
  const [temporary, setTemporary] = useState(emptyTemporary);
  const [pass, setPass] = useState(emptyPass);
  const [waitEdits, setWaitEdits] = useState({});
  const [slotEdits, setSlotEdits] = useState({});

  const load = async () => {
    try {
      const response = await api.get("/society-parking/bootstrap");
      setData(response.data);
      setSettings(
        response.data.settings || {
          vehicle_verification_required: true,
          waiting_list_enabled: true,
          eligible_relationships: [...RELATIONSHIPS],
          resident_rules: "",
        },
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load parking management",
      );
      setData({
        areas: [],
        slots: [],
        vehicles: [],
        allocations: [],
        waiting: [],
        temporary: [],
        passes: [],
        units: [],
        people: [],
        dashboard: {},
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const unitLabel = useMemo(
    () =>
      Object.fromEntries(
        (data?.units || []).map((item) => [item.unit_id, item.unit_number]),
      ),
    [data],
  );
  const personLabel = useMemo(
    () =>
      Object.fromEntries(
        (data?.people || []).map((item) => [item.person_id, item.display_name]),
      ),
    [data],
  );
  const vehicleLabel = useMemo(
    () =>
      Object.fromEntries(
        (data?.vehicles || []).map((item) => [
          item.vehicle_id,
          item.registration_number,
        ]),
      ),
    [data],
  );

  const saveArea = async () => {
    try {
      await api.post("/society-parking/areas", area);
      toast.success("Parking area created");
      setArea(emptyArea);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create parking area",
      );
    }
  };

  const saveSlot = async () => {
    try {
      await api.post("/society-parking/slots", slot);
      toast.success("Parking slot created");
      setSlot(emptySlot);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create parking slot",
      );
    }
  };

  const saveAllocation = async () => {
    try {
      await api.post("/society-parking/allocations", allocation);
      toast.success("Parking allocation created");
      setAllocation(emptyAllocation);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create allocation",
      );
    }
  };

  const decideVehicle = async (vehicleId, status) => {
    const reason = window.prompt(
      status === "REJECTED" ? "Rejection reason" : "Note (optional)",
    );
    if (reason === null) return;
    try {
      await api.post(`/society-parking/vehicles/${vehicleId}/decision`, {
        status,
        reason,
      });
      toast.success(`Vehicle ${status.toLowerCase()}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Vehicle update failed");
    }
  };

  const release = async (allocationId) => {
    const reason = window.prompt("Release reason");
    if (!reason) return;
    try {
      await api.post(`/society-parking/allocations/${allocationId}/release`, {
        reason,
      });
      toast.success("Allocation released");
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not release allocation",
      );
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/society-parking/settings", settings);
      toast.success("Parking settings saved");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save settings");
    }
  };

  const toggleRelationship = (value) => {
    const current = new Set(settings.eligible_relationships || []);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    setSettings({ ...settings, eligible_relationships: [...current] });
  };

  const saveTemporary = async () => {
    try {
      await api.post("/society-parking/temporary", temporary);
      toast.success("Temporary parking created");
      setTemporary(emptyTemporary);
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create temporary parking",
      );
    }
  };

  const issuePass = async () => {
    try {
      await api.post("/society-parking/passes", pass);
      toast.success("Parking pass issued");
      setPass(emptyPass);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not issue pass");
    }
  };

  const revokePass = async (passId) => {
    const reason = window.prompt("Revocation reason");
    if (!reason) return;
    try {
      await api.post(`/society-parking/passes/${passId}/revoke`, { reason });
      toast.success("Pass revoked");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not revoke pass");
    }
  };

  const updateWaiting = async (item) => {
    const edit = waitEdits[item.waiting_id] || {};
    const status = edit.status ?? item.status;
    const queue_sequence = Number(edit.queue_sequence ?? item.queue_sequence);
    const reason = edit.reason || "";
    try {
      await api.patch(`/society-parking/waiting-list/${item.waiting_id}`, {
        status,
        queue_sequence,
        reason,
      });
      toast.success("Waiting-list request updated");
      setWaitEdits((prev) => ({ ...prev, [item.waiting_id]: undefined }));
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update waiting-list",
      );
    }
  };

  const updateSlot = async (item) => {
    const status = slotEdits[item.slot_id]?.status ?? item.status;
    try {
      await api.patch(`/society-parking/slots/${item.slot_id}`, { status });
      toast.success("Slot status updated");
      setSlotEdits((prev) => ({ ...prev, [item.slot_id]: undefined }));
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update slot");
    }
  };

  if (!data) return <Loading />;
  const tabs = [
    ["dashboard", "Dashboard"],
    ["areas", "Areas & Slots"],
    ["vehicles", "Vehicles"],
    ["allocations", "Allocations"],
    ["waiting", "Waiting List"],
    ["temporary", "Temporary"],
    ["passes", "Passes"],
    ["settings", "Settings"],
    ["reports", "Reports"],
  ];
  return (
    <div data-testid="society-parking-page">
      <PageHeader
        title="Parking Management"
        subtitle="Operate parking inventory, resident vehicles, allocations, and parking credentials."
      />
      <div
        className="mb-6 flex flex-wrap gap-2"
        data-testid="parking-workspace-tabs"
      >
        {tabs.map(([value, label]) => (
          <Button
            data-testid={`parking-tab-${value}`}
            key={value}
            onClick={() => setTab(value)}
            size="sm"
            variant={tab === value ? "default" : "outline"}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === "dashboard" && (
        <section
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5"
          data-testid="parking-dashboard"
        >
          {[
            ["Total slots", data.dashboard.total_slots],
            ["Available", data.dashboard.available_slots],
            ["Allocated", data.dashboard.allocated_slots],
            ["Pending vehicles", data.dashboard.pending_vehicles],
            ["Waiting", data.dashboard.waiting],
          ].map(([label, value]) => (
            <article
              className="border bg-white p-4"
              data-testid={`parking-kpi-${label.toLowerCase().replaceAll(" ", "-")}`}
              key={label}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                {label}
              </p>
              <p className="mt-2 font-display text-3xl font-bold text-slate-900">
                {value || 0}
              </p>
            </article>
          ))}
        </section>
      )}

      {tab === "areas" && (
        <section className="grid gap-6 xl:grid-cols-[360px_420px_1fr]">
          <div className="border bg-white p-5" data-testid="parking-area-form">
            <h2 className="font-display text-xl font-semibold">Parking area</h2>
            <div className="mt-4 grid gap-3">
              <Input
                data-testid="parking-area-name"
                onChange={(event) =>
                  setArea({ ...area, name: event.target.value })
                }
                placeholder="Basement 1"
                value={area.name}
              />
              <Input
                data-testid="parking-area-location"
                onChange={(event) =>
                  setArea({ ...area, location: event.target.value })
                }
                placeholder="Location"
                value={area.location}
              />
              <Input
                data-testid="parking-area-level"
                onChange={(event) =>
                  setArea({ ...area, level: event.target.value })
                }
                placeholder="Floor / level"
                value={area.level}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="parking-area-save"
              onClick={saveArea}
            >
              <Plus className="h-4 w-4" />
              Add area
            </Button>
            <div className="mt-6 space-y-2" data-testid="parking-area-list">
              {data.areas.map((item) => (
                <div className="border p-3 text-sm" key={item.area_id}>
                  <b>{item.name}</b>
                  <p className="text-slate-500">
                    {item.location || item.level || "—"}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="border bg-white p-5" data-testid="parking-slot-form">
            <h2 className="font-display text-xl font-semibold">Parking slot</h2>
            <div className="mt-4 grid gap-3">
              <Input
                data-testid="parking-slot-number"
                onChange={(event) =>
                  setSlot({
                    ...slot,
                    slot_number: event.target.value.toUpperCase(),
                  })
                }
                placeholder="P-01"
                value={slot.slot_number}
              />
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-slot-area"
                onChange={(event) =>
                  setSlot({ ...slot, area_id: event.target.value })
                }
                value={slot.area_id}
              >
                <option value="">No area</option>
                {data.areas.map((item) => (
                  <option key={item.area_id} value={item.area_id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-slot-type"
                onChange={(event) =>
                  setSlot({
                    ...slot,
                    slot_type: event.target.value,
                    allowed_vehicle_types:
                      event.target.value === "TWO_WHEELER"
                        ? ["TWO_WHEELER"]
                        : ["CAR"],
                  })
                }
                value={slot.slot_type}
              >
                <option value="CAR">Car</option>
                <option value="TWO_WHEELER">Two-wheeler</option>
                <option value="FLEXIBLE">Flexible</option>
                <option value="VISITOR">Visitor</option>
                <option value="ACCESSIBLE">Accessible</option>
                <option value="OTHER">Other</option>
              </select>
              <Input
                data-testid="parking-slot-location"
                onChange={(event) =>
                  setSlot({ ...slot, location: event.target.value })
                }
                placeholder="Location"
                value={slot.location}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="parking-slot-save"
              onClick={saveSlot}
            >
              <MapPin className="h-4 w-4" />
              Add slot
            </Button>
          </div>
          <div
            className="overflow-x-auto border bg-white"
            data-testid="parking-slots-table"
          >
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="p-3">Slot</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Location</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Set status</th>
                </tr>
              </thead>
              <tbody>
                {data.slots.map((item) => (
                  <tr
                    className="border-t"
                    data-testid={`parking-slot-row-${item.slot_id}`}
                    key={item.slot_id}
                  >
                    <td className="p-3 font-medium">{item.slot_number}</td>
                    <td className="p-3">{item.slot_type}</td>
                    <td className="p-3">{item.location || "—"}</td>
                    <td className="p-3">
                      <Status value={item.status} />
                    </td>
                    <td className="p-3">
                      {item.status === "ALLOCATED" ? (
                        <span className="text-xs text-slate-500">
                          Managed by allocation
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                            data-testid={`parking-slot-status-select-${item.slot_id}`}
                            onChange={(event) =>
                              setSlotEdits((prev) => ({
                                ...prev,
                                [item.slot_id]: { status: event.target.value },
                              }))
                            }
                            value={
                              slotEdits[item.slot_id]?.status ?? item.status
                            }
                          >
                            {SLOT_STATUS_OPTIONS.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                          <Button
                            data-testid={`parking-slot-status-save-${item.slot_id}`}
                            onClick={() => updateSlot(item)}
                            size="sm"
                            variant="outline"
                          >
                            Save
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "vehicles" && (
        <section
          className="overflow-x-auto border bg-white"
          data-testid="parking-vehicles-table"
        >
          <table className="min-w-[850px] w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-3">Registration</th>
                <th className="p-3">Vehicle</th>
                <th className="p-3">Unit</th>
                <th className="p-3">Verification</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {data.vehicles.map((item) => (
                <tr
                  className="border-t"
                  data-testid={`parking-vehicle-row-${item.vehicle_id}`}
                  key={item.vehicle_id}
                >
                  <td className="p-3 font-mono font-medium">
                    {item.registration_number}
                  </td>
                  <td className="p-3">
                    {item.vehicle_type} ·{" "}
                    {[item.make, item.model].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="p-3">
                    {item.unit_snapshot?.unit_number || "—"}
                  </td>
                  <td className="p-3">
                    <Status value={item.verification_status} />
                  </td>
                  <td className="p-3">
                    {item.verification_status === "PENDING_VERIFICATION" && (
                      <div className="flex gap-2">
                        <Button
                          data-testid={`parking-vehicle-approve-${item.vehicle_id}`}
                          onClick={() =>
                            decideVehicle(item.vehicle_id, "APPROVED")
                          }
                          size="sm"
                        >
                          Approve
                        </Button>
                        <Button
                          data-testid={`parking-vehicle-reject-${item.vehicle_id}`}
                          onClick={() =>
                            decideVehicle(item.vehicle_id, "REJECTED")
                          }
                          size="sm"
                          variant="outline"
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {tab === "allocations" && (
        <section className="grid gap-6 xl:grid-cols-[380px_1fr]">
          <div
            className="border bg-white p-5"
            data-testid="parking-allocation-form"
          >
            <h2 className="font-display text-xl font-semibold">
              Allocate slot
            </h2>
            <div className="mt-4 grid gap-3">
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-allocation-slot"
                onChange={(event) =>
                  setAllocation({ ...allocation, slot_id: event.target.value })
                }
                value={allocation.slot_id}
              >
                <option value="">Select available slot</option>
                {data.slots
                  .filter((item) => item.status === "AVAILABLE")
                  .map((item) => (
                    <option key={item.slot_id} value={item.slot_id}>
                      {item.slot_number}
                    </option>
                  ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-allocation-unit"
                onChange={(event) =>
                  setAllocation({ ...allocation, unit_id: event.target.value })
                }
                value={allocation.unit_id}
              >
                <option value="">Select unit</option>
                {data.units.map((item) => (
                  <option key={item.unit_id} value={item.unit_id}>
                    {item.unit_number}
                  </option>
                ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-allocation-vehicle"
                onChange={(event) =>
                  setAllocation({
                    ...allocation,
                    vehicle_id: event.target.value,
                  })
                }
                value={allocation.vehicle_id}
              >
                <option value="">No vehicle linked</option>
                {data.vehicles
                  .filter((item) => item.verification_status === "APPROVED")
                  .map((item) => (
                    <option key={item.vehicle_id} value={item.vehicle_id}>
                      {item.registration_number}
                    </option>
                  ))}
              </select>
              <Input
                data-testid="parking-allocation-start"
                onChange={(event) =>
                  setAllocation({
                    ...allocation,
                    start_date: event.target.value,
                  })
                }
                type="date"
                value={allocation.start_date}
              />
              <Input
                data-testid="parking-allocation-end"
                onChange={(event) =>
                  setAllocation({ ...allocation, end_date: event.target.value })
                }
                type="date"
                value={allocation.end_date}
              />
              <Textarea
                data-testid="parking-allocation-reason"
                onChange={(event) =>
                  setAllocation({ ...allocation, reason: event.target.value })
                }
                placeholder="Allocation reason"
                value={allocation.reason}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="parking-allocation-save"
              onClick={saveAllocation}
            >
              <Car className="h-4 w-4" />
              Allocate
            </Button>
          </div>
          <div
            className="overflow-x-auto border bg-white"
            data-testid="parking-allocations-table"
          >
            <table className="min-w-[720px] w-full text-sm">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="p-3">Slot</th>
                  <th className="p-3">Unit / Vehicle</th>
                  <th className="p-3">Period</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {data.allocations.map((item) => (
                  <tr
                    className="border-t"
                    data-testid={`parking-allocation-row-${item.allocation_id}`}
                    key={item.allocation_id}
                  >
                    <td className="p-3 font-medium">
                      {item.slot_snapshot?.slot_number}
                    </td>
                    <td className="p-3">
                      {item.unit_snapshot?.unit_number} ·{" "}
                      {item.vehicle_snapshot?.registration_number ||
                        "No vehicle"}
                    </td>
                    <td className="p-3">
                      {item.start_date}{" "}
                      {item.end_date ? `– ${item.end_date}` : "· Permanent"}
                    </td>
                    <td className="p-3">
                      <Status value={item.status} />
                    </td>
                    <td className="p-3">
                      {item.status === "ACTIVE" && (
                        <Button
                          data-testid={`parking-allocation-release-${item.allocation_id}`}
                          onClick={() => release(item.allocation_id)}
                          size="sm"
                          variant="outline"
                        >
                          Release
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "waiting" && (
        <section
          className="border bg-white p-5"
          data-testid="parking-waiting-list"
        >
          <h2 className="font-display text-xl font-semibold">Waiting list</h2>
          <div className="mt-4 overflow-x-auto">
            {data.waiting.length === 0 ? (
              <p className="text-sm text-slate-500">
                No parking requests are waiting.
              </p>
            ) : (
              <table className="min-w-[900px] w-full text-sm">
                <thead className="bg-slate-100 text-left">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3">Unit</th>
                    <th className="p-3">Requested</th>
                    <th className="p-3">Requested on</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Lifecycle action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.waiting.map((item) => {
                    const edit = waitEdits[item.waiting_id] || {};
                    return (
                      <tr
                        className="border-t align-top"
                        data-testid={`parking-waiting-row-${item.waiting_id}`}
                        key={item.waiting_id}
                      >
                        <td className="p-3 font-medium">
                          {item.queue_sequence}
                        </td>
                        <td className="p-3">
                          {unitLabel[item.unit_id] || "—"}
                        </td>
                        <td className="p-3">
                          {item.requested_vehicle_type || "—"} /{" "}
                          {item.requested_slot_type || "—"}
                        </td>
                        <td className="p-3">
                          {(item.created_at || "").slice(0, 10) || "—"}
                        </td>
                        <td className="p-3">
                          <Status value={item.status} />
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                              data-testid={`parking-waiting-status-${item.waiting_id}`}
                              onChange={(event) =>
                                setWaitEdits((prev) => ({
                                  ...prev,
                                  [item.waiting_id]: {
                                    ...edit,
                                    status: event.target.value,
                                  },
                                }))
                              }
                              value={edit.status ?? item.status}
                            >
                              {WAITING_STATUS_OPTIONS.map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </select>
                            <Input
                              className="h-8 w-16"
                              data-testid={`parking-waiting-seq-${item.waiting_id}`}
                              onChange={(event) =>
                                setWaitEdits((prev) => ({
                                  ...prev,
                                  [item.waiting_id]: {
                                    ...edit,
                                    queue_sequence: event.target.value,
                                  },
                                }))
                              }
                              type="number"
                              value={edit.queue_sequence ?? item.queue_sequence}
                            />
                            <Input
                              className="h-8 w-40"
                              data-testid={`parking-waiting-reason-${item.waiting_id}`}
                              onChange={(event) =>
                                setWaitEdits((prev) => ({
                                  ...prev,
                                  [item.waiting_id]: {
                                    ...edit,
                                    reason: event.target.value,
                                  },
                                }))
                              }
                              placeholder="Reason"
                              value={edit.reason || ""}
                            />
                            <Button
                              data-testid={`parking-waiting-save-${item.waiting_id}`}
                              onClick={() => updateWaiting(item)}
                              size="sm"
                              variant="outline"
                            >
                              Apply
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {tab === "temporary" && (
        <section className="grid gap-6 xl:grid-cols-[380px_1fr]">
          <div
            className="border bg-white p-5"
            data-testid="parking-temporary-form"
          >
            <h2 className="font-display text-xl font-semibold">
              Temporary parking
            </h2>
            <div className="mt-4 grid gap-3">
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-temporary-unit"
                onChange={(event) =>
                  setTemporary({ ...temporary, unit_id: event.target.value })
                }
                value={temporary.unit_id}
              >
                <option value="">Select unit</option>
                {data.units.map((item) => (
                  <option key={item.unit_id} value={item.unit_id}>
                    {item.unit_number}
                  </option>
                ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-temporary-person"
                onChange={(event) =>
                  setTemporary({ ...temporary, person_id: event.target.value })
                }
                value={temporary.person_id}
              >
                <option value="">Person (optional)</option>
                {data.people.map((item) => (
                  <option key={item.person_id} value={item.person_id}>
                    {item.display_name}
                  </option>
                ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-temporary-vehicle"
                onChange={(event) =>
                  setTemporary({
                    ...temporary,
                    vehicle_id: event.target.value,
                  })
                }
                value={temporary.vehicle_id}
              >
                <option value="">Vehicle (optional)</option>
                {data.vehicles.map((item) => (
                  <option key={item.vehicle_id} value={item.vehicle_id}>
                    {item.registration_number}
                  </option>
                ))}
              </select>
              <Input
                data-testid="parking-temporary-registration"
                onChange={(event) =>
                  setTemporary({
                    ...temporary,
                    registration_number: event.target.value.toUpperCase(),
                  })
                }
                placeholder="Registration (if no vehicle selected)"
                value={temporary.registration_number}
              />
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-temporary-slot"
                onChange={(event) =>
                  setTemporary({ ...temporary, slot_id: event.target.value })
                }
                value={temporary.slot_id}
              >
                <option value="">Slot (optional)</option>
                {data.slots.map((item) => (
                  <option key={item.slot_id} value={item.slot_id}>
                    {item.slot_number}
                  </option>
                ))}
              </select>
              <label className="text-xs font-semibold text-slate-500">
                Valid from (society local time)
              </label>
              <Input
                data-testid="parking-temporary-from"
                onChange={(event) =>
                  setTemporary({ ...temporary, valid_from: event.target.value })
                }
                type="datetime-local"
                value={temporary.valid_from}
              />
              <label className="text-xs font-semibold text-slate-500">
                Valid until (society local time)
              </label>
              <Input
                data-testid="parking-temporary-until"
                onChange={(event) =>
                  setTemporary({
                    ...temporary,
                    valid_until: event.target.value,
                  })
                }
                type="datetime-local"
                value={temporary.valid_until}
              />
              <Textarea
                data-testid="parking-temporary-reason"
                onChange={(event) =>
                  setTemporary({ ...temporary, reason: event.target.value })
                }
                placeholder="Reason"
                value={temporary.reason}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="parking-temporary-save"
              onClick={saveTemporary}
            >
              <Clock className="h-4 w-4" />
              Create temporary parking
            </Button>
          </div>
          <div
            className="overflow-x-auto border bg-white"
            data-testid="parking-temporary-table"
          >
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="p-3">Unit</th>
                  <th className="p-3">Registration</th>
                  <th className="p-3">Slot</th>
                  <th className="p-3">Validity (local)</th>
                  <th className="p-3">Reason</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.temporary.length === 0 ? (
                  <tr>
                    <td className="p-3 text-slate-500" colSpan={6}>
                      No temporary parking records.
                    </td>
                  </tr>
                ) : (
                  data.temporary.map((item) => (
                    <tr
                      className="border-t"
                      data-testid={`parking-temporary-row-${item.temporary_id}`}
                      key={item.temporary_id}
                    >
                      <td className="p-3">
                        {item.unit_snapshot?.unit_number ||
                          unitLabel[item.unit_id] ||
                          "—"}
                      </td>
                      <td className="p-3 font-mono">
                        {item.registration_number || "—"}
                      </td>
                      <td className="p-3">
                        {data.slots.find((s) => s.slot_id === item.slot_id)
                          ?.slot_number || "—"}
                      </td>
                      <td className="p-3">
                        {(item.valid_from_local || "").replace("T", " ")} →{" "}
                        {(item.valid_until_local || "").replace("T", " ")}
                      </td>
                      <td className="p-3">{item.reason || "—"}</td>
                      <td className="p-3">
                        <Status value={item.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "passes" && (
        <section className="grid gap-6 xl:grid-cols-[360px_1fr]">
          <div className="border bg-white p-5" data-testid="parking-pass-form">
            <h2 className="font-display text-xl font-semibold">
              Issue parking pass
            </h2>
            <div className="mt-4 grid gap-3">
              <Input
                data-testid="parking-pass-number"
                onChange={(event) =>
                  setPass({
                    ...pass,
                    pass_number: event.target.value.toUpperCase(),
                  })
                }
                placeholder="PASS-001"
                value={pass.pass_number}
              />
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-pass-vehicle"
                onChange={(event) =>
                  setPass({ ...pass, vehicle_id: event.target.value })
                }
                value={pass.vehicle_id}
              >
                <option value="">Select approved vehicle</option>
                {data.vehicles
                  .filter((item) => item.verification_status === "APPROVED")
                  .map((item) => (
                    <option key={item.vehicle_id} value={item.vehicle_id}>
                      {item.registration_number}
                    </option>
                  ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="parking-pass-slot"
                onChange={(event) =>
                  setPass({ ...pass, slot_id: event.target.value })
                }
                value={pass.slot_id}
              >
                <option value="">Slot (optional)</option>
                {data.slots.map((item) => (
                  <option key={item.slot_id} value={item.slot_id}>
                    {item.slot_number}
                  </option>
                ))}
              </select>
              <label className="text-xs font-semibold text-slate-500">
                Expiry date (optional)
              </label>
              <Input
                data-testid="parking-pass-expiry"
                onChange={(event) =>
                  setPass({ ...pass, expiry_date: event.target.value })
                }
                type="date"
                value={pass.expiry_date}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="parking-pass-issue"
              onClick={issuePass}
            >
              <TicketCheck className="h-4 w-4" />
              Issue pass
            </Button>
          </div>
          <div
            className="overflow-x-auto border bg-white"
            data-testid="parking-passes-list"
          >
            <table className="min-w-[720px] w-full text-sm">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <th className="p-3">Pass</th>
                  <th className="p-3">Vehicle</th>
                  <th className="p-3">Unit</th>
                  <th className="p-3">Expiry</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {data.passes.length === 0 ? (
                  <tr>
                    <td className="p-3 text-slate-500" colSpan={6}>
                      No parking passes issued.
                    </td>
                  </tr>
                ) : (
                  data.passes.map((item) => (
                    <tr
                      className="border-t"
                      data-testid={`parking-pass-row-${item.pass_id}`}
                      key={item.pass_id}
                    >
                      <td className="p-3 font-mono font-medium">
                        {item.pass_number}
                      </td>
                      <td className="p-3 font-mono">
                        {vehicleLabel[item.vehicle_id] || "—"}
                      </td>
                      <td className="p-3">
                        {unitLabel[item.unit_id] || "—"}
                      </td>
                      <td className="p-3">{item.expiry_date || "—"}</td>
                      <td className="p-3">
                        <Status value={item.status} />
                      </td>
                      <td className="p-3">
                        {item.status === "ACTIVE" && (
                          <Button
                            data-testid={`parking-pass-revoke-${item.pass_id}`}
                            onClick={() => revokePass(item.pass_id)}
                            size="sm"
                            variant="outline"
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "settings" && settings && (
        <section
          className="max-w-2xl border bg-white p-6"
          data-testid="parking-settings-form"
        >
          <h2 className="font-display text-xl font-semibold">
            Parking settings
          </h2>
          <div className="mt-5 space-y-5">
            <label
              className="flex items-center gap-3"
              data-testid="parking-settings-verification"
            >
              <input
                checked={!!settings.vehicle_verification_required}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    vehicle_verification_required: event.target.checked,
                  })
                }
                type="checkbox"
              />
              <span className="text-sm">
                Require admin verification for resident vehicles
              </span>
            </label>
            <label
              className="flex items-center gap-3"
              data-testid="parking-settings-waitlist"
            >
              <input
                checked={!!settings.waiting_list_enabled}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    waiting_list_enabled: event.target.checked,
                  })
                }
                type="checkbox"
              />
              <span className="text-sm">Enable parking waiting list</span>
            </label>
            <div>
              <p className="text-sm font-semibold text-slate-700">
                Eligible resident relationships
              </p>
              <div
                className="mt-2 flex flex-wrap gap-3"
                data-testid="parking-settings-relationships"
              >
                {RELATIONSHIPS.map((value) => (
                  <label
                    className="flex items-center gap-2 text-sm"
                    key={value}
                  >
                    <input
                      checked={(
                        settings.eligible_relationships || []
                      ).includes(value)}
                      data-testid={`parking-settings-relationship-${value.toLowerCase()}`}
                      onChange={() => toggleRelationship(value)}
                      type="checkbox"
                    />
                    {value.replaceAll("_", " ")}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">
                Resident parking rules
              </p>
              <Textarea
                className="mt-2"
                data-testid="parking-settings-rules"
                onChange={(event) =>
                  setSettings({ ...settings, resident_rules: event.target.value })
                }
                placeholder="Guidelines shown to residents"
                rows={5}
                value={settings.resident_rules || ""}
              />
            </div>
          </div>
          <Button
            className="mt-6"
            data-testid="parking-settings-save"
            onClick={saveSettings}
          >
            <Save className="h-4 w-4" />
            Save settings
          </Button>
        </section>
      )}

      {tab === "reports" && (
        <section className="border bg-white p-5" data-testid="parking-reports">
          <h2 className="font-display text-xl font-semibold">
            Parking registers
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              "slots",
              "vehicles",
              "allocations",
              "waiting",
              "temporary",
              "passes",
            ].map((kind) => (
              <a
                data-testid={`parking-report-${kind}`}
                href={`${API}/society-parking/reports/register?kind=${kind}&export=csv`}
                key={kind}
              >
                <Button size="sm" variant="outline">
                  <FileDown className="h-4 w-4" />
                  {kind}
                </Button>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
